// src/pages/seeker/SeekerWorkPreferences.jsx
// Work Preferences — auto-saves each field
// Route: /seeker/profile/work-preferences

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { seekerAPI } from '../../services/api';
import toast from 'react-hot-toast';

// ── UI primitives ──────────────────────────────────────

const SectionTitle = ({ children }) => (
  <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '16px', paddingBottom: '8px', borderBottom: '2px solid var(--border)' }}>
    {children}
  </div>
);

const FL = ({ label, children, hint }) => (
  <div style={{ marginBottom: '14px' }}>
    <label className="form-label" style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 600, color: 'var(--primary)', fontSize: '12px' }}>
      {label}{hint && <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: '6px' }}>{hint}</span>}
    </label>
    {children}
  </div>
);

// ── Checkbox group ────────────────────────────────────
const CheckboxGroup = ({ options, selected, onChange }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
    {options.map(o => {
      const checked = selected.includes(o.value);
      return (
        <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: 'var(--text)' }}>
          <input type="checkbox" checked={checked}
            onChange={() => {
              const next = checked ? selected.filter(v => v !== o.value) : [...selected, o.value];
              onChange(next);
            }}
            style={{ width: '16px', height: '16px', accentColor: 'var(--primary)' }} />
          {o.label}
        </label>
      );
    })}
  </div>
);

// ── Autocomplete text input ────────────────────────────
const AutoInput = ({ value, onChange, onBlur, placeholder, suggestions }) => {
  const [show, setShow] = useState(false);
  const [filtered, setFiltered] = useState([]);
  const ref = useRef();

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    if (v.trim()) {
      setFiltered(suggestions.filter(s => s.toLowerCase().includes(v.toLowerCase())).slice(0, 8));
      setShow(true);
    } else {
      setShow(false);
    }
  };

  const pick = (s) => { onChange(s); setShow(false); if (onBlur) onBlur(s); };

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setShow(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        type="text"
        value={value}
        onChange={handleChange}
        onBlur={() => { setTimeout(() => setShow(false), 150); if (onBlur) onBlur(value); }}
        placeholder={placeholder}
        className="form-input"
      />
      {show && filtered.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: 'white', border: '1px solid var(--border)', borderRadius: '3px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '200px', overflowY: 'auto' }}>
          {filtered.map((s, i) => (
            <div key={i} onMouseDown={() => pick(s)}
              style={{ padding: '8px 12px', fontSize: '13px', cursor: 'pointer', color: 'var(--text)' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f4f7fb'}
              onMouseLeave={e => e.currentTarget.style.background = 'white'}>
              {s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Benefits pill selector ─────────────────────────────
const BenefitsPicker = ({ selected, onChange }) => {
  const OPTIONS = [
    'Health Insurance', 'Dental', 'Vision', 'Life Insurance',
    'Superannuation / Pension', 'Paid Leave', 'Flexible Hours',
    'Remote Work', 'Work From Home Allowance', 'Gym / Wellness',
    'Professional Development', 'Study Leave', 'Stock Options / Equity',
    'Bonus / Performance Pay', 'Parental Leave', 'Childcare Support',
    'Employee Discounts', 'Company Car / Car Allowance', 'Phone Allowance',
    'Meal Allowance', 'Relocation Assistance', 'Visa Sponsorship',
  ];
  const toggle = (b) => {
    const next = selected.includes(b) ? selected.filter(x => x !== b) : [...selected, b];
    onChange(next);
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
      {OPTIONS.map(b => {
        const active = selected.includes(b);
        return (
          <button key={b} type="button" onClick={() => toggle(b)}
            style={{ padding: '4px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '20px', cursor: 'pointer', border: active ? '1.5px solid var(--primary)' : '1.5px solid var(--border)', background: active ? 'var(--primary)' : 'white', color: active ? 'white' : 'var(--text-muted)', transition: 'all 0.15s' }}>
            {b}
          </button>
        );
      })}
    </div>
  );
};

// ── Multi-item list with autocomplete add ──────────────
const MultiItemList = ({ items, onAdd, onRemove, placeholder, suggestions }) => {
  const [inputVal, setInputVal] = useState('');

  const handleAdd = (val) => {
    const trimmed = val.trim();
    if (trimmed && !items.includes(trimmed)) {
      onAdd(trimmed);
      setInputVal('');
    } else {
      setInputVal('');
    }
  };

  return (
    <div>
      {items.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
          {items.map((item, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px', fontSize: '12px', fontWeight: 600, borderRadius: '20px', background: 'var(--primary)', color: 'white' }}>
              {item}
              <button type="button" onClick={() => onRemove(item)}
                style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: 0, opacity: 0.8 }}>×</button>
            </span>
          ))}
        </div>
      )}
      <AutoInput
        value={inputVal}
        onChange={setInputVal}
        onBlur={handleAdd}
        placeholder={placeholder}
        suggestions={suggestions.filter(s => !items.includes(s))}
      />
    </div>
  );
};

// ── Multi-item list with async autocomplete + freetext ──
const AsyncMultiItemList = ({ items, onAdd, onRemove, placeholder, fetchSuggestions }) => {
  const [inputVal, setInputVal] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [show, setShow] = useState(false);
  const ref = useRef();
  const debounceRef = useRef(null);

  const handleChange = (val) => {
    setInputVal(val);
    clearTimeout(debounceRef.current);
    if (val.trim().length >= 2) {
      debounceRef.current = setTimeout(async () => {
        try {
          const res = await fetchSuggestions(val.trim());
          const list = res.data.data || [];
          setSuggestions(list.filter(s => !items.includes(s)));
          setShow(true);
        } catch { setSuggestions([]); }
      }, 300);
    } else {
      setSuggestions([]);
      setShow(false);
    }
  };

  const handleAdd = (val) => {
    const trimmed = (val || inputVal).trim();
    if (trimmed && !items.includes(trimmed)) {
      onAdd(trimmed);
      setInputVal('');
      setSuggestions([]);
      setShow(false);
    } else {
      setInputVal('');
    }
  };

  const pick = (s) => { handleAdd(s); };

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setShow(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div>
      {items.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
          {items.map((item, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px', fontSize: '12px', fontWeight: 600, borderRadius: '20px', background: 'var(--primary)', color: 'white' }}>
              {item}
              <button type="button" onClick={() => onRemove(item)}
                style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: 0, opacity: 0.8 }}>×</button>
            </span>
          ))}
        </div>
      )}
      <div ref={ref} style={{ position: 'relative' }}>
        <input
          type="text"
          value={inputVal}
          onChange={e => handleChange(e.target.value)}
          onBlur={() => { setTimeout(() => setShow(false), 150); handleAdd(inputVal); }}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(inputVal); } }}
          placeholder={placeholder}
          className="form-input"
        />
        {show && suggestions.length > 0 && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: 'white', border: '1px solid var(--border)', borderRadius: '3px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '200px', overflowY: 'auto' }}>
            {suggestions.map((s, i) => (
              <div key={i} onMouseDown={() => pick(s)}
                style={{ padding: '8px 12px', fontSize: '13px', cursor: 'pointer', color: 'var(--text)' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f4f7fb'}
                onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                {s}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Suggestion lists ───────────────────────────────────
const LANGUAGES = ['English', 'Mandarin', 'Spanish', 'Hindi', 'Arabic', 'French', 'Bengali', 'Portuguese', 'Russian', 'Urdu', 'Indonesian', 'German', 'Japanese', 'Swahili', 'Marathi', 'Telugu', 'Turkish', 'Tamil', 'Vietnamese', 'Korean', 'Italian', 'Thai', 'Malay', 'Tagalog', 'Persian'];

const INDUSTRIES = ['Accounting & Finance', 'Administration', 'Advertising & Marketing', 'Agriculture', 'Architecture & Design', 'Arts & Entertainment', 'Banking', 'Construction', 'Consulting', 'Customer Service', 'Defence & Military', 'Education & Training', 'Engineering', 'Government & Public Sector', 'Healthcare & Medical', 'Hospitality & Tourism', 'Human Resources', 'Information Technology', 'Insurance', 'Legal', 'Logistics & Supply Chain', 'Manufacturing', 'Media & Communications', 'Mining & Resources', 'Non-Profit', 'Pharmaceuticals', 'Real Estate', 'Retail & Consumer Goods', 'Sales', 'Science & Research', 'Sport & Fitness', 'Telecommunications', 'Transport', 'Utilities & Energy'];

const WORKPLACE_SIZE_OPTIONS = [
  { value: 'SMALL',      label: 'Small (2–5)' },
  { value: 'MODEST',     label: 'Modest (5–10)' },
  { value: 'MEDIUM',     label: 'Medium (10–20)' },
  { value: 'LARGE',      label: 'Large (20–50)' },
  { value: 'VERY_LARGE', label: 'Very large (50+)' },
];

const ENVIRONMENT_OPTIONS = [
  { value: 'OFFICE',          label: 'Office' },
  { value: 'TRANSPORTATION',  label: 'Transportation (car / truck / train / bus / boat etc.)' },
  { value: 'EDUCATIONAL',     label: 'Educational (schools / universities etc.)' },
  { value: 'RETAIL',          label: 'Retail (shopping centre, stores etc.)' },
  { value: 'HOSPITALITY',     label: 'Hospitality / Hotel' },
  { value: 'MANUFACTURING',   label: 'Manufacturing (factory)' },
  { value: 'GOVERNMENT',      label: 'Government' },
  { value: 'CONSTRUCTION',    label: 'Construction' },
  { value: 'OUTDOORS',        label: 'Outdoors' },
  { value: 'HEALTHCARE',      label: 'Healthcare (hospitals, medical centers etc.)' },
  { value: 'FARMING',         label: 'Farming / Agriculture' },
  { value: 'OTHER',           label: 'Other' },
];

const SCHEDULE_OPTIONS = [
  { value: 'FULL_TIME',  label: 'Full-time' },
  { value: 'PART_TIME',  label: 'Part-time' },
  { value: 'CONTRACT',   label: 'Contract' },
  { value: 'CASUAL',     label: 'Casual' },
  { value: 'INTERNSHIP', label: 'Internship' },
];

const EMPLOYER_TYPE_OPTIONS = [
  { value: 'PRIVATE',    label: 'Private Sector' },
  { value: 'PUBLIC',     label: 'Public Sector / Government' },
  { value: 'NON_PROFIT', label: 'Non-Profit / NGO' },
  { value: 'STARTUP',    label: 'Startup' },
  { value: 'AGENCY',     label: 'Recruitment Agency' },
];

const ADMIN_LEVEL_OPTIONS = [
  { value: 'VOLUNTEER',         label: 'Volunteer' },
  { value: 'OFFICER',           label: 'Officer' },
  { value: 'SUPERVISOR',        label: 'Supervisor' },
  { value: 'ASSISTANT_MANAGER', label: 'Assistant Manager' },
  { value: 'MANAGER',           label: 'Manager' },
  { value: 'SENIOR_MANAGER',    label: 'Senior Manager' },
  { value: 'EXECUTIVE_MANAGER', label: 'Executive Manager' },
];

// ── Location row ──────────────────────────────────────
const LocationRow = ({ loc, onChange, onRemove }) => (
  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '8px' }}>
    <div style={{ flex: 1 }}>
      <input value={loc.state || ''} onChange={e => onChange({ ...loc, state: e.target.value })}
        placeholder="State / Province" className="form-input" />
    </div>
    <div style={{ flex: 1 }}>
      <input value={loc.city || ''} onChange={e => onChange({ ...loc, city: e.target.value })}
        placeholder="City" className="form-input" />
    </div>
    <button type="button" onClick={onRemove}
      style={{ background: 'none', border: 'none', color: '#CC0000', cursor: 'pointer', fontSize: '18px', lineHeight: 1, padding: '8px 4px', flexShrink: 0 }}>×</button>
  </div>
);

// ══════════════════════════════════════════════════════
export const SeekerWorkPreferences = () => {
  const [loading, setLoading] = useState(true);
  const saveTimer = useRef(null);

  const [prefs, setPrefs] = useState({
    workplaceSize:       [],
    workEnvironment:     [],
    workingLanguages:    [],
    locations:           [],
    willingToRelocate:   true,
    benefits:            [],
    salaryMin:           '',
    salaryMax:           '',
    schedule:            ['FULL_TIME'],
    industries:          [],
    employerType:        [],
    positionNames:       [],
    adminLevels:         [],
    employerNames:       [],
  });

  // ── Save helpers ──────────────────────────────────────
  const savePrefs = useCallback((nextPrefs) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await seekerAPI.updateProfile({ workPreferences: nextPrefs });
        toast.success('Saved', { duration: 1200, id: 'autosave' });
      } catch (e) {
        toast.error(e?.response?.data?.message || 'Save failed');
      }
    }, 600);
  }, []);

  const immediateSet = (field) => (val) => {
    setPrefs(prev => {
      const next = { ...prev, [field]: val };
      savePrefs(next);
      return next;
    });
  };

  const blurSave = (field) => (val) => {
    setPrefs(prev => {
      const next = { ...prev, [field]: val };
      savePrefs(next);
      return next;
    });
  };

  // ── Load ──────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const res = await seekerAPI.getProfile();
        const p = res.data.data;
        const wp = p.workPreferences || {};

        // Migrate old single-value fields to arrays
        const toArr = (v, fallback) => Array.isArray(v) ? v : (v ? [v] : fallback);

        setPrefs({
          workplaceSize:     toArr(wp.workplaceSize || wp.companySize, WORKPLACE_SIZE_OPTIONS.map(o => o.value)),
          workEnvironment:   toArr(wp.workEnvironment, []),
          workingLanguages:  toArr(wp.workingLanguages || (wp.workingLanguage ? [wp.workingLanguage] : null), []),
          locations:         Array.isArray(wp.locations) ? wp.locations : [],
          willingToRelocate: wp.willingToRelocate !== undefined ? wp.willingToRelocate : true,
          benefits:          Array.isArray(wp.benefits) ? wp.benefits : [],
          salaryMin:         wp.salaryMin || '',
          salaryMax:         wp.salaryMax || '',
          schedule:          toArr(wp.schedule, []),
          industries:        toArr(wp.industries || (wp.industry ? [wp.industry] : null), []),
          employerType:      toArr(wp.employerType, []),
          positionNames:     Array.isArray(wp.positionNames) ? wp.positionNames : [],
          adminLevels:       toArr(wp.adminLevels, []),
          employerNames:     Array.isArray(wp.employerNames) ? wp.employerNames : [],
        });
      } catch { toast.error('Failed to load preferences'); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  // ── Location handlers ─────────────────────────────────
  const updateLocation = (index, loc) => {
    const next = [...prefs.locations];
    next[index] = loc;
    immediateSet('locations')(next);
  };
  const removeLocation = (index) => {
    immediateSet('locations')(prefs.locations.filter((_, i) => i !== index));
  };
  const addLocation = () => {
    immediateSet('locations')([...prefs.locations, { state: '', city: '' }]);
  };

  if (loading) return <div style={{ padding: '40px', color: 'var(--text-muted)' }}>Loading…</div>;

  const card = { background: 'white', border: '1px solid var(--border)', borderRadius: '6px', padding: '20px 24px', marginBottom: '16px' };

  return (
    <div className="fade-in">
      <div className="page-header">
        <h1>Work Preferences</h1>
        <p>Changes are saved automatically.</p>
      </div>
    <div className="page-content">

      {/* ── Workplace ── */}
      <div style={card}>
        <SectionTitle>Workplace</SectionTitle>

        <FL label="Workplace Size">
          <CheckboxGroup options={WORKPLACE_SIZE_OPTIONS} selected={prefs.workplaceSize} onChange={immediateSet('workplaceSize')} />
        </FL>

        <FL label="Workplace Environment">
          <CheckboxGroup options={ENVIRONMENT_OPTIONS} selected={prefs.workEnvironment} onChange={immediateSet('workEnvironment')} />
        </FL>

        <FL label="Working Language">
          <MultiItemList
            items={prefs.workingLanguages}
            onAdd={(lang) => immediateSet('workingLanguages')([...prefs.workingLanguages, lang])}
            onRemove={(lang) => immediateSet('workingLanguages')(prefs.workingLanguages.filter(l => l !== lang))}
            placeholder="Type to add a language…"
            suggestions={LANGUAGES}
          />
        </FL>

        <FL label="Workplace Location">
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontSize: '13px', color: 'var(--text)', cursor: 'pointer' }}>
            <input type="checkbox" checked={prefs.willingToRelocate}
              onChange={e => immediateSet('willingToRelocate')(e.target.checked)}
              style={{ width: '16px', height: '16px', accentColor: 'var(--primary)' }} />
            Willing to relocate
          </label>
          {prefs.locations.map((loc, i) => (
            <LocationRow key={i} loc={loc} onChange={(l) => updateLocation(i, l)} onRemove={() => removeLocation(i)} />
          ))}
          <button type="button" onClick={addLocation}
            style={{ background: 'none', border: '1px dashed var(--border)', borderRadius: '4px', padding: '6px 14px', fontSize: '12px', color: 'var(--primary)', cursor: 'pointer', fontWeight: 600 }}>
            + Add location
          </button>
        </FL>

        <FL label="Benefits">
          <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: '0 0 8px' }}>Select all that matter to you.</p>
          <BenefitsPicker selected={prefs.benefits} onChange={immediateSet('benefits')} />
        </FL>

      </div>

      {/* ── Position & Employer ── */}
      <div style={{ ...card, marginBottom: '40px' }}>
        <SectionTitle>Position & Employer</SectionTitle>

        <FL label="Position Name">
          <AsyncMultiItemList
            items={prefs.positionNames}
            onAdd={(name) => immediateSet('positionNames')([...prefs.positionNames, name])}
            onRemove={(name) => immediateSet('positionNames')(prefs.positionNames.filter(n => n !== name))}
            placeholder="Type to add a position name…"
            fetchSuggestions={(q) => seekerAPI.autocompletePositionNames(q)}
          />
        </FL>

        <FL label="Admin Level">
          <CheckboxGroup options={ADMIN_LEVEL_OPTIONS} selected={prefs.adminLevels} onChange={immediateSet('adminLevels')} />
        </FL>

        <FL label="Favourite Companies for Whom to Work">
          <AsyncMultiItemList
            items={prefs.employerNames}
            onAdd={(name) => immediateSet('employerNames')([...prefs.employerNames, name])}
            onRemove={(name) => immediateSet('employerNames')(prefs.employerNames.filter(n => n !== name))}
            placeholder="Type to add a company name…"
            fetchSuggestions={(q) => seekerAPI.autocompleteEmployerNames(q)}
          />
        </FL>

        <div className="form-row">
          <FL label="Salary — Minimum">
            <input type="number" min="0" step="1000"
              value={prefs.salaryMin}
              onChange={e => setPrefs(p => ({ ...p, salaryMin: e.target.value }))}
              onBlur={e => blurSave('salaryMin')(e.target.value)}
              placeholder="e.g. 60000"
              className="form-input" />
          </FL>

          <FL label="Salary — Maximum">
            <input type="number" min="0" step="1000"
              value={prefs.salaryMax}
              onChange={e => setPrefs(p => ({ ...p, salaryMax: e.target.value }))}
              onBlur={e => blurSave('salaryMax')(e.target.value)}
              placeholder="e.g. 90000"
              className="form-input" />
          </FL>
        </div>

        <FL label="Schedule">
          <CheckboxGroup options={SCHEDULE_OPTIONS} selected={prefs.schedule} onChange={immediateSet('schedule')} />
        </FL>

        <FL label="Industry">
          <MultiItemList
            items={prefs.industries}
            onAdd={(ind) => immediateSet('industries')([...prefs.industries, ind])}
            onRemove={(ind) => immediateSet('industries')(prefs.industries.filter(i => i !== ind))}
            placeholder="Type to add an industry…"
            suggestions={INDUSTRIES}
          />
        </FL>

        <FL label="Employer Type">
          <CheckboxGroup options={EMPLOYER_TYPE_OPTIONS} selected={prefs.employerType} onChange={immediateSet('employerType')} />
        </FL>

      </div>

    </div>
    </div>
  );
};
